import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, MFA_SIGN_IN_PENDING } from '@core/services/auth.service';
import { RecaptchaService } from '@core/services/recaptcha.service';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoggingService } from '@core/services/logging.service';

@Component({
  selector: 'app-email-signin',
  templateUrl: './email-signin.component.html',
  styleUrl: './email-signin.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EmailSigninComponent {
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private recaptchaService = inject(RecaptchaService);
  private loggingService = inject(LoggingService);

  form: FormGroup;
  loading = false;
  serverMessage: any | undefined;

  constructor() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: [
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(15), // Enhanced security policy requirement (Firebase minimum is 6)
        ]),
      ],
      passwordConfirm: ['', []],
    });
  }

  get email() {
    return this.form.get('email');
  }
  get password() {
    return this.form.get('password');
  }

  get passwordValid() {
    return this.form.controls['password'].errors === null;
  }

  get requiredValid() {
    return !this.form.controls['password'].hasError('required');
  }

  get minLengthValid() {
    const passwordControl = this.form.controls['password'];
    return (
      passwordControl.value &&
      passwordControl.value.length >= 15 &&
      !passwordControl.hasError('minlength')
    );
  }

  /**
   * Handles the submission of email and password for login or signup. It prevents
   * the use of public email providers and checks if the user already exists.
   *
   * @returns A boolean indicating the success of the operation.
   */
  /**
   * Runs reCAPTCHA v3 and has the API verify the token before sign-in (#870).
   * With no site key configured the check is skipped and logged, so a missing
   * key degrades loudly in logs rather than locking everyone out. Once a key is
   * configured, sign-in requires server-side verification.
   *
   * @throws Error whose message contains 'reCAPTCHA' when verification fails.
   */
  private async verifyHuman(): Promise<void> {
    if (!(await this.recaptchaService.isConfigured())) {
      if (environment.production) {
        this.loggingService.logError('reCAPTCHA site key not configured; sign-in is unprotected');
      }
      return;
    }

    const token = await this.recaptchaService.execute('login');
    if (!token) {
      throw new Error('reCAPTCHA verification failed');
    }
    if (token === 'dev-bypass') {
      return; // Non-production builds only (see RecaptchaService.execute).
    }

    const verified = await firstValueFrom(this.recaptchaService.verify(token, 'login'));
    if (!verified) {
      throw new Error('reCAPTCHA verification failed');
    }
  }

  async onSubmit() {
    this.loading = true;

    const email = this.email ? this.email.value : null;
    const password = this.password ? this.password.value : null;

    // Check if both email and password are provided
    if (!email || !password) {
      this.serverMessage = 'Enter Email and Password';
      this.loading = false;
      return false;
    }

    try {
      await this.verifyHuman();

      // Attempt to sign in with the provided email and password
      const signInResult = await this.authService.signInWithEmailPassword(
        email,
        password,
      );

      // AuthService already navigated to the MFA challenge — do not override
      // with /logging-in. An unverified email is not a stop (#943).
      if (signInResult === MFA_SIGN_IN_PENDING) {
        this.loading = false;
        return true;
      }

      await this.router.navigate(['/logging-in'], { replaceUrl: true });
    } catch (err: any) {
      // Handle reCAPTCHA-specific errors
      if (err.message?.includes('reCAPTCHA')) {
        this.loggingService.logError('reCAPTCHA verification failed', { error: err });
        this.serverMessage = 'Security verification failed. Please try again or contact support.';
        this.loading = false;
        return false;
      }

      if (err.code === 'auth/user-not-found') {
        // Use generic error message to avoid leaking system information
        this.serverMessage = 'Invalid email or password. Please try again.';
        this.loading = false;
        return false;
      }

      if (err.code === 'auth/wrong-password') {
        // Use generic error message to avoid leaking system information
        this.serverMessage = 'Invalid email or password. Please try again.';
        this.loading = false;
        return false;
      }

      // Handle other errors with generic message (avoid exposing Firebase error details)
      this.loggingService.logError('Login error', { code: err.code, message: err.message });
      this.serverMessage = 'An error occurred during login. Please try again or contact support.';
      this.loading = false;
      return false;
    }

    // Reset the loading state and potentially handle successful login/signup
    this.loading = false;
    return true;
  }
}
